import { IconButton, Menu, useTheme } from '@mui/material';

import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { ActionMenuItem, type ActionMenuItemProps } from './component/ActionMenuItem';
import useActionMenu from './hooks/useActionMenu';
import { Icon, IconType } from '../../mui-wrapper-components/Icon/Icon';

export type ActionMenuProps = {
  /**
   * @props {string} id - id of the icon, used to create testid
   */
  id: string;

  /**
   * @props {boolean} disabled - disable the menu
   * @default false
   */
  disabled?: boolean;

  /**
   * @props {string} iconType - name of the icon
   * @default 'verticalDots'
   */
  iconType?: IconType;

  /**
   * @props {number} iconWidth - width of the icon
   * @default 32
   */
  iconWidth?: number;

  /**
   * @props {number} iconHeight - height of the icon
   * @default 32
   */
  iconHeight?: number;

  /**
   * @props {string} iconBorderRadius - border radius of the icon
   * @default '8'
   */
  iconBorderRadius?: number;

  /**
   * @props {string} iconBackgroundColor - background color of the icon
   * @default 'transparent'
   */
  iconBackgroundColor?: string;

  /**
   * @props {string} iconHoverBackgroundColor - background color of the icon when hovered
   * @default 'rgb(0 0 0 / 4%)'
   */
  iconHoverBackgroundColor?: string;

  /**
   * @props {string} iconHighlightBackgroundColor - background color of the icon
   * @default 'rgb(211 212 215 / 60%)'
   */
  iconHighlightBackgroundColor?: string;

  /**
   * @props {'top' | 'center' | 'bottom' } anchorVertical - where the popup menu should be anchored vertically on the icon
   * @default 'bottom'
   */
  anchorVertical?: 'top' | 'center' | 'bottom';

  /**
   * @props {'left' | 'center' | 'right' } anchorHorizontal - where the popup menu should be anchored horizontally on the icon
   * @default 'right'
   */
  anchorHorizontal?: 'left' | 'center' | 'right';

  /**
   * @props {'top' | 'center' | 'bottom' } transformVertical - where the popup menu should be transformed vertically on the icon, when the menu is open
   * @default 'top'
   */
  transformVertical?: 'top' | 'center' | 'bottom';

  /**
   * @props {'left' | 'center' | 'right' } transformHorizontal - where the popup menu should be transformed horizontally on the icon, when the menu is open
   * @default 'right'
   */
  transformHorizontal?: 'left' | 'center' | 'right';

  /**
   * @props {number} menuWidth - width of the menu
   * @default 220
   */
  menuWidth?: number;

  /**
   * @props {ActionMenuItem[]} menuItems - menu items to be displayed
   */
  menuItems?: ActionMenuItemProps[];

  /**
   * @props {boolean} stopClickEventPropagation - stop click event propagation
   */
  stopClickEventPropagation?: boolean;
};

export function ActionMenu(props: ActionMenuProps) {
  const theme = useTheme();
  const {
    id,
    disabled = false,
    iconType = 'verticalDots',
    iconWidth = 32,
    iconHeight = 32,
    iconBorderRadius = 8,
    iconBackgroundColor = 'transparent',
    iconHoverBackgroundColor = 'rgb(0 0 0 / 4%)',
    iconHighlightBackgroundColor = 'rgb(211 212 215 / 60%)',
    menuWidth = 220,
    anchorVertical = 'bottom',
    anchorHorizontal = 'right',
    transformVertical = 'top',
    transformHorizontal = 'right',
    menuItems = [],
    stopClickEventPropagation,
  } = props;

  const { anchorEl, isMenuOpen, handleClick, handleClose } = useActionMenu();

  const _onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    stopClickEventPropagation && event.stopPropagation();
    handleClick(event);
  };

  return (
    <>
      <IconButton
        data-testid={`${id}-actionMenu`}
        aria-label="actionMenu"
        id={`${id}-actionMenu-icon`}
        key={`${id}-actionMenu-icon`}
        aria-controls={`${id}-actionMenu-icon`}
        aria-haspopup="true"
        onClick={_onClick}
        disabled={disabled}
        sx={{
          backgroundColor: `${
            anchorEl && anchorEl.getAttribute('id') === `${id}-actionMenu-icon`
              ? `${iconHighlightBackgroundColor}`
              : `${iconBackgroundColor}`
          }`,
          '&:hover': { backgroundColor: `${iconHoverBackgroundColor}` },
          borderRadius: `${iconBorderRadius}px`,
          width: `${iconWidth}px`,
          height: `${iconHeight}px`,
        }}
      >
        <Icon id={`${id}-actionMenu-iconType`} icon={`${iconType}`} />
      </IconButton>
      <Menu
        id={`${id}-actionMenu-menu`}
        key={`${id}-actionMenu-menu`}
        anchorEl={anchorEl}
        transformOrigin={{ horizontal: transformHorizontal, vertical: transformVertical }}
        anchorOrigin={{ horizontal: anchorHorizontal, vertical: anchorVertical }}
        open={
          isMenuOpen && Boolean(anchorEl && anchorEl.getAttribute('id') === `${id}-actionMenu-icon`)
        }
        onClose={handleClose}
        slotProps={{
          paper: {
            sx: {
              // TODO: no VDS token for this value — needs designer input (multi-layer menu shadow)
              boxShadow:
                '0px 5px 5px -3px rgba(0, 0, 0, 0.20), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
              border: `1px solid ${theme.palette.neutral[100]}`,
              borderRadius: `${borderRadius.sm}px`,
              width: `${menuWidth}px`,
              marginTop: '3px',
            },
          },
        }}
      >
        {menuItems.map((menuItem) => (
          <ActionMenuItem key={`${menuItem.id}-key`} handleClose={handleClose} {...menuItem} />
        ))}
      </Menu>
    </>
  );
}

export default ActionMenu;
